import React from 'react';
import styled from 'styled-components';
import { ClipLoader } from 'react-spinners';

const DropzoneContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Dropzone = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isDragActive',
})`
  border: 2px dashed ${props => props.isDragActive 
    ? 'var(--color-primary)' 
    : 'var(--color-border)'};
  border-radius: var(--border-radius-md);
  background-color: ${props => props.isDragActive 
    ? 'rgba(0, 113, 227, 0.05)' 
    : 'var(--color-background)'};
  padding: var(--spacing-xl);
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  cursor: pointer;
  transition: var(--transition-default);
  margin-bottom: var(--spacing-lg);
  
  &:hover {
    border-color: var(--color-primary);
    background-color: rgba(0, 113, 227, 0.05);
  }
`;

const UploadIcon = styled.div`
  margin-bottom: var(--spacing-md);
  color: var(--color-primary);
  
  svg {
    width: 48px;
    height: 48px;
  }
`;

const UploadText = styled.p`
  margin-bottom: var(--spacing-sm);
  font-weight: 500;
`;

const UploadHint = styled.p`
  color: var(--color-text-secondary);
  font-size: 0.875rem;
  margin-bottom: 0;
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  padding: var(--spacing-md);
  background-color: rgba(0, 113, 227, 0.05);
  border-radius: var(--border-radius-sm);
  margin-bottom: var(--spacing-md);
  width: 100%;
`;

const FileIcon = styled.div`
  margin-right: var(--spacing-md);
  color: var(--color-primary);
`;

const FileDetails = styled.div`
  flex: 1;
`;

const FileName = styled.p`
  font-weight: 500;
  margin-bottom: var(--spacing-xs);
`;

const FileSize = styled.p`
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  margin: 0;
`;

const UploadButton = styled.button`
  min-width: 120px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background-color: var(--color-border);
  border-radius: 4px;
  overflow: hidden;
  margin-top: var(--spacing-md);
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${props => props.progress}%;
  background-color: var(--color-primary);
  border-radius: 4px;
  transition: width 0.2s ease;
`;

const ProgressLabel = styled.p`
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  margin-top: var(--spacing-xs);
  margin-bottom: 0;
`;

// Helper function to format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const UploadArea = ({ 
  getRootProps, 
  getInputProps, 
  isDragActive, 
  file, 
  uploading, 
  uploadProgress = 0, 
  handleUpload 
}) => {
  return (
    <DropzoneContainer>
      {!file && (
        <Dropzone {...getRootProps()} isDragActive={isDragActive}>
          <input {...getInputProps()} />
          <UploadIcon>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
          </UploadIcon>
          <UploadText>
            {isDragActive 
              ? 'Drop your video here' 
              : 'Drag & drop your video here or click to browse'}
          </UploadText>
          <UploadHint>Supports AVI and MP4 video files up to 100MB</UploadHint>
        </Dropzone>
      )}
      
      {file && (
        <>
          <FileInfo>
            <FileIcon>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 10.5V6H9V10.5L12 12.5L15 10.5Z" fill="currentColor" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M4 22H20C20.5304 22 21.0391 21.7893 21.4142 21.4142C21.7893 21.0391 22 20.5304 22 20V4C22 3.46957 21.7893 2.96086 21.4142 2.58579C21.0391 2.21071 20.5304 2 20 2H4C3.46957 2 2.96086 2.21071 2.58579 2.58579C2.21071 2.96086 2 3.46957 2 4V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 18H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </FileIcon>
            <FileDetails>
              <FileName>{file.name}</FileName>
              <FileSize>{formatFileSize(file.size)}</FileSize>
            </FileDetails>
          </FileInfo>
          
          <UploadButton 
            className="btn btn-primary"
            onClick={handleUpload}
            disabled={uploading}
          >
            {uploading ? (
              <>
                <ClipLoader size={16} color="#ffffff" css="margin-right: 8px;" />
                Uploading... {uploadProgress}%
              </>
            ) : 'Generate Transcript'}
          </UploadButton>
          {uploading && (
            <>
              <ProgressBar>
                <ProgressFill progress={uploadProgress} />
              </ProgressBar>
              <ProgressLabel>Uploading your video...</ProgressLabel>
            </>
          )}
        </>
      )}
    </DropzoneContainer>
  );
};

export default UploadArea;